'use client';

import { createContext, useContext, useMemo } from 'react';

import { Capabilities, type Capability } from '../index';
import { resolveCapabilityPolicy, type CapabilityPolicy } from '../lib/capability-policy';

const CapabilitiesContext = createContext<ReadonlySet<Capability> | null>(null);
const CapabilityPoliciesContext = createContext<Partial<Record<Capability, CapabilityPolicy>>>({});

interface CapabilitiesProviderProps {
  /** Resolved capability keys for the current tenant. Must come from the server. */
  capabilities: readonly Capability[];
  capabilityPolicies?: Partial<Record<Capability, CapabilityPolicy>>;
  children: React.ReactNode;
}

export function CapabilitiesProvider({ capabilities, capabilityPolicies = {}, children }: CapabilitiesProviderProps) {
  const set = useMemo(() => new Set(capabilities), [capabilities]);
  return <CapabilitiesContext.Provider value={set}><CapabilityPoliciesContext.Provider value={capabilityPolicies}>{children}</CapabilityPoliciesContext.Provider></CapabilitiesContext.Provider>;
}

export function useStaffCashCollection(locationId?: string | null, options?: { allowTenantWide?: boolean }): boolean {
  const policy = useContext(CapabilityPoliciesContext)[Capabilities.STAFF_CASH_COLLECTION];
  const defaultEnabled = useCapabilityOptional(Capabilities.STAFF_CASH_COLLECTION);
  return policy ? resolveCapabilityPolicy(policy, locationId, options) : defaultEnabled;
}

export function useStaffCashCollectionForLocations(locationIds: readonly (string | null | undefined)[]): boolean {
  return useCapabilityForLocations(Capabilities.STAFF_CASH_COLLECTION, locationIds);
}

export function useCapabilityAtLocation(key: Capability, locationId?: string | null): boolean {
  const policy = useContext(CapabilityPoliciesContext)[key];
  const defaultEnabled = useCapabilityOptional(key);
  return policy ? resolveCapabilityPolicy(policy, locationId) : defaultEnabled;
}

export function useCapabilityForLocations(key: Capability, locationIds: readonly (string | null | undefined)[]): boolean {
  const policy = useContext(CapabilityPoliciesContext)[key];
  const defaultEnabled = useCapabilityOptional(key);
  if (locationIds.length === 0) return false;
  return locationIds.every((locationId) => policy
    ? resolveCapabilityPolicy(policy, locationId)
    : defaultEnabled);
}

/** Returns true if the capability is enabled for the current tenant. */
export function useCapability(key: Capability): boolean {
  const ctx = useContext(CapabilitiesContext);
  if (ctx === null) {
    throw new Error('useCapability must be used within a CapabilitiesProvider');
  }
  return ctx.has(key);
}

/**
 * Same as `useCapability` but returns `false` when no provider is mounted.
 * Useful for shared components rendered both inside and outside the tenant tree.
 */
export function useCapabilityOptional(key: Capability): boolean {
  const ctx = useContext(CapabilitiesContext);
  return ctx?.has(key) ?? false;
}

/**
 * Same as `useCapabilityOptional` but returns `fallback` when no provider is
 * mounted, for shared components that also render outside the tenant tree
 * (e.g. the native tab bar on /auth pages) and must keep their default there.
 */
export function useCapabilityOr(key: Capability, fallback: boolean): boolean {
  const ctx = useContext(CapabilitiesContext);
  return ctx ? ctx.has(key) : fallback;
}

/** The full enabled-capability set for the current tenant. */
export function useCapabilities(): ReadonlySet<Capability> {
  const ctx = useContext(CapabilitiesContext);
  if (ctx === null) {
    throw new Error('useCapabilities must be used within a CapabilitiesProvider');
  }
  return ctx;
}
